import { DOCUMENT_ID_LENGTH_MAX, DOCUMENT_TYPE_LENGTH_MAX, JSON_DEPTH_MAX, JSON_NODE_COUNT_MAX, PORTABLE_BLOB_BYTES_MAX, PORTABLE_BLOB_COUNT_MAX, PORTABLE_TOTAL_BLOB_BYTES_MAX, QUERY_LIMIT_DEFAULT, QUERY_LIMIT_MAX, } from './constants.js';
import { StorageError } from './errors.js';
const HASH_PATTERN = /^sha256:[a-f0-9]{64}$/u;
const BASE64_PATTERN = /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/u;
function invalid(detail) {
    throw new StorageError('invalid-data', { detail });
}
function assertTimestamp(value, field) {
    if (typeof value !== 'string' || !Number.isFinite(Date.parse(value)))
        invalid(`${field} must be an ISO timestamp`);
}
export function assertDocumentIdentity(type, id) {
    if (typeof type !== 'string' || type.length === 0 || type.length > DOCUMENT_TYPE_LENGTH_MAX) {
        invalid(`Document type must contain 1-${DOCUMENT_TYPE_LENGTH_MAX} characters`);
    }
    if (typeof id !== 'string' || id.length === 0 || id.length > DOCUMENT_ID_LENGTH_MAX || id.includes('\u0000')) {
        invalid(`Document id must contain 1-${DOCUMENT_ID_LENGTH_MAX} safe characters`);
    }
}
export function assertJsonValue(value) {
    const stack = [{ value, depth: 0 }];
    const seen = new WeakSet();
    let nodeCount = 0;
    while (stack.length > 0) {
        const current = stack.pop();
        if (!current)
            break;
        nodeCount += 1;
        if (nodeCount > JSON_NODE_COUNT_MAX)
            invalid('Document data is too large');
        if (current.depth > JSON_DEPTH_MAX)
            invalid('Document data is nested too deeply');
        const item = current.value;
        if (item === null || typeof item === 'string' || typeof item === 'boolean')
            continue;
        if (typeof item === 'number' && Number.isFinite(item))
            continue;
        if (typeof item !== 'object')
            invalid('Document data must be JSON-compatible');
        if (seen.has(item))
            invalid('Document data cannot contain cycles');
        seen.add(item);
        if (Array.isArray(item)) {
            for (const child of item)
                stack.push({ value: child, depth: current.depth + 1 });
            continue;
        }
        const prototype = Object.getPrototypeOf(item);
        if (prototype !== Object.prototype && prototype !== null)
            invalid('Document data must use plain objects');
        for (const child of Object.values(item))
            stack.push({ value: child, depth: current.depth + 1 });
    }
}
export function assertStoredDocument(value) {
    if (!value || typeof value !== 'object')
        invalid('Stored document must be an object');
    const document = value;
    assertDocumentIdentity(document.type, document.id);
    if (!Number.isSafeInteger(document.version) || (document.version ?? 0) < 1) {
        invalid('Document version must be positive');
    }
    assertTimestamp(document.createdAt, 'createdAt');
    assertTimestamp(document.updatedAt, 'updatedAt');
    if (Date.parse(document.updatedAt) < Date.parse(document.createdAt))
        invalid('updatedAt cannot precede createdAt');
    if (document.deletedAt !== null) {
        assertTimestamp(document.deletedAt, 'deletedAt');
        if (Date.parse(document.deletedAt) > Date.parse(document.updatedAt))
            invalid('deletedAt cannot follow updatedAt');
    }
    assertJsonValue(document.data);
}
export function assertOutboxOperation(value) {
    if (!value || typeof value !== 'object')
        invalid('Outbox operation must be an object');
    const operation = value;
    if (typeof operation.opId !== 'string'
        || operation.opId.length === 0
        || operation.opId.length > DOCUMENT_ID_LENGTH_MAX) {
        invalid('opId is invalid');
    }
    if (!Number.isSafeInteger(operation.sequence) || (operation.sequence ?? 0) < 1) {
        invalid('Outbox sequence must be positive');
    }
    if (operation.kind !== 'document-put' && operation.kind !== 'document-delete')
        invalid('Outbox kind is invalid');
    assertDocumentIdentity(operation.documentType, operation.documentId);
    if (!Number.isSafeInteger(operation.documentVersion) || (operation.documentVersion ?? 0) < 1) {
        invalid('Outbox documentVersion must be positive');
    }
    assertTimestamp(operation.createdAt, 'Outbox createdAt');
    assertStoredDocument(operation.payload);
    if (operation.payload.type !== operation.documentType || operation.payload.id !== operation.documentId) {
        invalid('Outbox payload identity does not match the operation');
    }
    if (operation.payload.version !== operation.documentVersion)
        invalid('Outbox payload version does not match');
    if (operation.kind === 'document-put' && operation.payload.deletedAt !== null) {
        invalid('Put operation cannot contain a tombstone');
    }
    if (operation.kind === 'document-delete' && operation.payload.deletedAt === null) {
        invalid('Delete operation must contain a tombstone');
    }
}
export function assertBlobRef(value) {
    if (!value || typeof value !== 'object')
        invalid('Blob reference must be an object');
    const ref = value;
    if (typeof ref.mime !== 'string' || ref.mime.length === 0 || ref.mime.length > 255)
        invalid('Blob mime is invalid');
    if (!Number.isSafeInteger(ref.size) || (ref.size ?? -1) < 0 || (ref.size ?? 0) > PORTABLE_BLOB_BYTES_MAX) {
        invalid('Blob size is outside the portable limit');
    }
    if (typeof ref.hash !== 'string' || !HASH_PATTERN.test(ref.hash))
        invalid('Blob hash is invalid');
    const expectedId = `blob_${ref.hash.slice('sha256:'.length)}`;
    if (ref.id !== expectedId)
        invalid('Blob id does not match its hash');
    assertTimestamp(ref.createdAt, 'Blob createdAt');
}
export function normalizeQueryWindow(limit, offset) {
    const normalizedLimit = limit ?? QUERY_LIMIT_DEFAULT;
    const normalizedOffset = offset ?? 0;
    if (!Number.isSafeInteger(normalizedLimit) || normalizedLimit < 1 || normalizedLimit > QUERY_LIMIT_MAX) {
        throw new StorageError('limit-exceeded');
    }
    if (!Number.isSafeInteger(normalizedOffset) || normalizedOffset < 0 || normalizedOffset > QUERY_LIMIT_MAX) {
        throw new StorageError('limit-exceeded');
    }
    return { limit: normalizedLimit, offset: normalizedOffset };
}
export function assertPortableDataset(value) {
    if (!value || typeof value !== 'object')
        invalid('Dataset must be an object');
    const dataset = value;
    if (dataset.format !== 'tchoma-storage' || dataset.formatVersion !== 1)
        invalid('Dataset format is unsupported');
    if (dataset.mode !== 'local-only' && dataset.mode !== 'sync-ready')
        invalid('Dataset mode is invalid');
    if (!Number.isSafeInteger(dataset.schemaVersion) || (dataset.schemaVersion ?? -1) < 0) {
        invalid('schemaVersion is invalid');
    }
    assertTimestamp(dataset.exportedAt, 'exportedAt');
    if (!Array.isArray(dataset.documents) || dataset.documents.length > QUERY_LIMIT_MAX) {
        invalid('Document list is invalid');
    }
    if (!Array.isArray(dataset.outbox) || dataset.outbox.length > QUERY_LIMIT_MAX)
        invalid('Outbox list is invalid');
    if (!Array.isArray(dataset.blobs) || dataset.blobs.length > PORTABLE_BLOB_COUNT_MAX)
        invalid('Blob list is invalid');
    const documentKeys = new Set();
    for (const document of dataset.documents) {
        assertStoredDocument(document);
        const key = `${document.type}\u0000${document.id}`;
        if (documentKeys.has(key))
            invalid('Dataset contains duplicate documents');
        documentKeys.add(key);
    }
    const opIds = new Set();
    let previousSequence = 0;
    for (const operation of dataset.outbox) {
        assertOutboxOperation(operation);
        if (opIds.has(operation.opId))
            invalid('Dataset contains duplicate operation ids');
        if (operation.sequence <= previousSequence)
            invalid('Outbox operations must be ordered by sequence');
        opIds.add(operation.opId);
        previousSequence = operation.sequence;
    }
    const blobIds = new Set();
    let totalBlobBytes = 0;
    for (const portableBlob of dataset.blobs) {
        if (!portableBlob || typeof portableBlob !== 'object')
            invalid('Portable blob is invalid');
        assertBlobRef(portableBlob.ref);
        if (blobIds.has(portableBlob.ref.id))
            invalid('Dataset contains duplicate blobs');
        if (typeof portableBlob.dataBase64 !== 'string' || !BASE64_PATTERN.test(portableBlob.dataBase64)) {
            invalid('Blob data is not valid base64');
        }
        const decodedSize = Math.floor(portableBlob.dataBase64.length * 3 / 4)
            - (portableBlob.dataBase64.endsWith('==') ? 2 : portableBlob.dataBase64.endsWith('=') ? 1 : 0);
        if (decodedSize !== portableBlob.ref.size)
            invalid('Blob base64 size does not match its reference');
        totalBlobBytes += decodedSize;
        if (totalBlobBytes > PORTABLE_TOTAL_BLOB_BYTES_MAX)
            throw new StorageError('limit-exceeded');
        blobIds.add(portableBlob.ref.id);
    }
}
//# sourceMappingURL=validation.js.map